<script lang="ts">
//JSDOC
/**
 * @category Home / 0.Top Bar
 * @subcategory WRAPPER
 * @module component-TopBar
 * @desc
 * ![TOPBAR](jsdoc-images/topbar.png)
 * - ### [AgentKPI.vue](module-component-AgentKPI.html#)
 * - ### [TrafficLight.vue](module-component-TrafficLight.html#)
 * - ### [AgentToggleState.vue](module-component-AgentToggleState.html#)
 * - ### [UserPopup](module-UserPopup.html#)
 */
</script>

<script setup lang="ts">
import { defineAsyncComponent, computed, ref } from 'vue';
import posteLogo from '@/assets/poste-logo.png';
import { storeToRefs } from 'pinia';
import { useContactStore } from '@/stores/contact';
import { useAgentStore } from '@/stores/agent';
import { FEATURE_TASK_ALERT_COUNTERS } from '@/constants/featureFlags';
import { eventBus } from '@/utils/common';

const AgentKPI = defineAsyncComponent(() => import('@/components/layout/molecules/AgentKPI.vue'));
const ServiceAreaKPI = defineAsyncComponent(() => import('@/components/layout/molecules/ServiceAreaKPI.vue'));
const TrafficLight = defineAsyncComponent(() => import('@/components/layout/atoms/TrafficLight.vue'));
const AgentToggleState = defineAsyncComponent(() => import('@/components/layout/molecules/AgentToggleState.vue'));
const UserPopup = defineAsyncComponent(() => import('@/components/layout/molecules/UserPopup.vue'));

// Get current contact to check if it's a TASK
const contactStore = useContactStore();
const { currentContactGetter } = storeToRefs(contactStore);

// Get support enabled state from agent store
const agentStore = useAgentStore();
const { supportEnabled } = storeToRefs(agentStore);

// Support button state
const isSupportTabOpen = ref(false);

const openSupportTab = () => {
  isSupportTabOpen.value = true;
  eventBus.emit('supportTabRequested');
};

// Listen for support tab closed event
eventBus.on('supportTabClosed', () => {
  isSupportTabOpen.value = false;
});

/**
 * Show Service Area KPI counters for:
 * - TASK channel contacts
 * - ONLY if feature flag is enabled
 */
const showServiceAreaCounters = computed(() => {
  // Early return if feature flag is disabled
  if (!FEATURE_TASK_ALERT_COUNTERS) {
    return false;
  }

  return currentContactGetter.value?.channel === 'TASK';
});
</script>

<template>
  <q-bar
    class="bg-warning top-bar"
    style="padding: 30px; position: sticky;"
  >
    <div class="flex gap-6">
      <AgentKPI />
      <ServiceAreaKPI v-if="showServiceAreaCounters" />
    </div>
    <q-space />
    <div class="flex justify-center text-weight-bold">
      <img
        alt="Poste Italiane"
        :src="posteLogo"
        style="height: 15px;"
      >
    </div>
    <q-space />
    <div class="row items-center">
      <Card
        v-if="supportEnabled"
        class="bg-negative text-white q-mr-sm cursor-pointer supporto-button"
        :class="{ 'supporto-disabled': isSupportTabOpen }"
        :card-title-class="'q-pa-sm'"
        @click="!isSupportTabOpen && openSupportTab()"
      >
        <template #title>
          Supporto Tecnico
        </template>
      </Card>
      <TrafficLight class="mr-4" />
      <AgentToggleState class="mx-2" />
      <UserPopup />
    </div>
  </q-bar>
</template>
<style scoped>
.top-bar {
  padding: 30px;
  position: sticky !important;
  top: 0 !important;
  z-index: 2 !important;
  border-bottom: 2px solid white;
}

.supporto-button {
  opacity: 1;
  transition: opacity 0.3s;
}

.supporto-button.supporto-disabled {
  opacity: 0.6;
  cursor: not-allowed !important;
}
</style>